import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SearchIcon from "@mui/icons-material/Search";
import IconButton from '@mui/material/IconButton';


const RWANDA_BLUE = "#00A1DE";
const SECTION_BACKGROUND = "#FFFFFF";

const HeroSection = ({ isMobile }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();

  const sliderContent = [
    {
      type: 'image',
      media: "/hero/knowledge-portal.jpg",
      title: 'Knowledge Portal',
      subtitle: 'Access comprehensive HR resources and government services',
      ctaText: 'Explore Resources',
      ctaLink: '/resources'
    },
    {
      type: 'video',
      media: "/hero/rwanda-hr.mp4",
      poster: "/hero/video-poster.jpg",
      title: 'HR Management System',
      subtitle: 'Streamlined processes for recruitment, payroll, and more',
      ctaText: 'Watch Overview',
      ctaLink: '/overview'
    },
    {
      type: 'image',
      media: "/hero/government-services.jpg",
      title: 'Government Services',
      subtitle: 'Efficient systems for public sector management',
      ctaText: 'Learn More',
      ctaLink: '/services'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % sliderContent.length);
        setFadeIn(true);
      }, 500);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        height: isMobile ? '90vh' : '100vh',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
      }}
    >
      {/* Background Media */}
      {sliderContent[currentSlide].type === 'video' ? (
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          poster={sliderContent[currentSlide].poster}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            minWidth: '100%',
            minHeight: '100%',
            width: 'auto',
            height: 'auto',
            transform: 'translate(-50%, -50%)',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={sliderContent[currentSlide].media} type="video/mp4" />
        </Box>
      ) : (
        <Box
          component="img"
          src={sliderContent[currentSlide].media}
          alt=""
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
      )}

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(195deg, ${alpha('#0a2e52', 0.85)} 0%, ${alpha(RWANDA_BLUE, 0.5)} 100%)`,
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '1200px',
          px: isMobile ? 4 : 6,
          textAlign: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: fadeIn ? 1 : 0, y: fadeIn ? 0 : 20 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant={isMobile ? "h3" : "h2"}
            sx={{
              fontWeight: 700,
              mb: 3,
              textShadow: '0 2px 10px rgba(0,0,0,0.3)',
              fontSize: isMobile ? '2rem' : '3.5rem',
              lineHeight: 1.2,
            }}
          >
            {sliderContent[currentSlide].title}
          </Typography>
          
          <Typography
            variant={isMobile ? "body1" : "h5"}
            sx={{
              mb: 4,
              opacity: 0.9,
              textShadow: '0 1px 5px rgba(0,0,0,0.3)',
              maxWidth: '800px',
              mx: 'auto',
              fontSize: isMobile ? '1rem' : '1.5rem',
            }}
          >
            {sliderContent[currentSlide].subtitle}
          </Typography>
        </motion.div>

        {/* Search and CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: fadeIn ? 1 : 0, y: fadeIn ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: alpha(theme.palette.common.white, 0.9),
                borderRadius: '50px',
                px: 2,
                py: 1,
                width: '100%',
                maxWidth: '500px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 1),
                }
              }}
            >
              <IconButton sx={{ color: theme.palette.primary.main }}>
                <SearchIcon />
              </IconButton>
              <input
                type="text"
                placeholder="Search knowledge base..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  width: '100%',
                  padding: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  color: theme.palette.text.primary,
                }}
              />
            </Box>
            
            <Button
              variant="contained"
              endIcon={
                sliderContent[currentSlide].type === 'video' ? 
                <PlayCircleOutlineIcon /> : 
                <ArrowForwardIcon />
              }
              sx={{
                backgroundColor: RWANDA_BLUE,
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                color: 'white',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: alpha(RWANDA_BLUE, 0.9),
                }
              }}
              href={sliderContent[currentSlide].ctaLink}
            >
              {sliderContent[currentSlide].ctaText}
            </Button>
          </Box>
        </motion.div>

        {/* Slide indicators */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            zIndex: 3,
            gap: 1,
          }}
        >
          {sliderContent.map((_, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={() => {
                setFadeIn(false);
                setTimeout(() => {
                  setCurrentSlide(index);
                  setFadeIn(true);
                }, 500);
              }}
              sx={{
                p: 0,
                color: currentSlide === index ? RWANDA_BLUE : 'rgba(255,255,255,0.5)',
                '&:hover': {
                  color: currentSlide === index ? RWANDA_BLUE : 'rgba(255,255,255,0.8)',
                }
              }}
            >
              <Box
                sx={{
                  width: currentSlide === index ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: 'currentColor',
                  transition: 'all 0.3s ease',
                }}
              />
            </IconButton>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const CardSection = ({
  index,
  title,
  description,
  image,
  link,
  isMobile,
  reverseLayout,
}) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  const variants = {
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    hidden: { opacity: 0, y: 50 },
  };

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        py: isMobile ? 6 : 10,
        px: isMobile ? 2 : 6,
        background: SECTION_BACKGROUND,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Parallax background element */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url('/assets/image.jpg')`,  
          backgroundSize: "cover",  
          backgroundPosition: "center",  
          zIndex: 0,
        }}
        animate={{
          y: [0, -20, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      <Box
        sx={{
          maxWidth: "xl",
          mx: "auto",
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: isMobile
            ? "column"
            : reverseLayout
            ? "row-reverse"
            : "row",
          alignItems: "center",
          gap: isMobile ? 4 : 8,
        }}
      >
        {/* Card Component */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={variants}
          style={{ flex: 1 }}
        >
          <Card
            sx={{
              borderRadius: "16px",
              border: `1px solid ${alpha(RWANDA_BLUE, 0.1)}`,
              boxShadow: "0 8px 24px rgba(0, 161, 222, 0.1)",
              overflow: "hidden",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 28px rgba(0, 161, 222, 0.15)",
              },
            }}
            component="a"
            href={link}
            elevation={0}
          >
            <Box
              sx={{
                width: "100%",
                height: isMobile ? "200px" : "300px",
                background: `linear-gradient(135deg, ${alpha(
                  RWANDA_BLUE,
                  0.1
                )} 0%, ${alpha(RWANDA_BLUE, 0.05)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              {image && (
                <img
                  src={image}
                  alt={title}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>
          </Card>
        </motion.div>

        {/* Text Content */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={variants}
          transition={{ delay: 0.2 }}
          style={{ flex: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              textAlign: isMobile ? "center" : reverseLayout ? "right" : "left",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: RWANDA_BLUE,
                fontSize: isMobile ? "1.75rem" : "2.125rem",
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#5a6d85",
                lineHeight: 1.7,
                fontSize: isMobile ? "1rem" : "1.1rem",
              }}
            >
              {description}
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: isMobile ? "center" : reverseLayout ? "flex-end" : "flex-start",
              }}
            >
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: RWANDA_BLUE,
                  borderRadius: "8px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  color: "white",
                  "&:hover": {
                    backgroundColor: alpha(RWANDA_BLUE, 0.9),
                  },
                }}
                href={link}
              >
                Learn More
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

const KnowledgePortalSections = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

   
  const cardData = [
    {
      title: "RECRUITMENT",
      description:
        "Explore the organizational framework of Rwanda's government institutions and their functions.",
      image: "/path/to/government-structure-icon.svg",
      link: "/government-structure",
    },
    {
      title: "ORGANIZATIONAL STRUCTURE",
      description:
        "Learn about Rwanda's economic policies, growth strategies, and investment opportunities.",
      image: "/path/to/economic-development-icon.svg",
      link: "/economic-development",
    },
    {
      title: "PAYROLL",
      description:
        "Discover Rwanda's education policies, institutions, and academic programs.",
      image: "/path/to/education-icon.svg",
      link: "/education-system",
    },
    {
      title: "RBM",
      description:
        "Experience Rwanda's rich cultural heritage and breathtaking tourism destinations.",
      image: "/path/to/tourism-icon.svg",
      link: "/tourism-culture",
    },
    {
      title: "LEAVE AND ABSENCE",
      description:
        "Rwanda's advancements in technology, digital transformation, and innovation ecosystem.",
      image: "/path/to/technology-icon.svg",
      link: "/technology-innovation",
    },
    {
      title: "EXIT AND TERMINATION",
      description:
        "Learn about Rwanda's agricultural policies, practices, and food security initiatives.",
      image: "/path/to/agriculture-icon.svg",
      link: "/agriculture",
    },
    {
      title: "CONTRACT MANAGEMENT",
      description:
        "Discover Rwanda's infrastructure development projects and urban planning strategies.",
      image: "/path/to/infrastructure-icon.svg",
      link: "/infrastructure",
    },
    {
      title: "DISCIPLINE",
      description:
        "Rwanda's international relations, diplomatic missions, and global partnerships.",
      image: "/path/to/foreign-policy-icon.svg",
      link: "/foreign-policy",
    },
    {
      title: "TRAINING",
      description:
        "Rwanda's international relations, diplomatic missions, and global partnerships.",
      image: "/path/to/foreign-policy-icon.svg",
      link: "/foreign-policy",
    }
  ];

  return (
    <Box component="main">
      {/* Enhanced Hero Section */}
      <HeroSection isMobile={isMobile} />

      {/* Card Sections */}
      {cardData.map((data, index) => (
        <CardSection
          key={index}
          index={index}
          title={data.title}
          description={data.description}
          image={data.image}
          link={data.link}
          isMobile={isMobile}
          reverseLayout={index % 2 !== 0} // Alternate layout
        />
      ))}
    </Box>
  );
};

export default KnowledgePortalSections;